import { Box, LinearProgress, Stack, Theme, useTheme } from "@mui/material";
import moment from "moment";
import { ReactNode, useCallback, useMemo } from "react";
import {
  EventPropGetter,
  Formats,
  Messages,
  Event as RBCEvent,
  Calendar as RBCalendar,
  View,
  ViewStatic,
  Views,
  momentLocalizer,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { CalendarContext, CalendarContextValue } from "@/common/contexts/calendar";
import { useCalendar } from "@/common/hooks/useCalendar";
import { DateRange } from "@/common/ts/datetime";

import AppPopover from "../Popover/AppPopover";
import { rbcGlobalStyles } from "./Calendar.styles";
import { CalendarEntryDetails } from "./CalendarEntryDetails";
import { CalendarEntryPopoverContent } from "./CalendarEntryPopoverContent";
import CalendarToolbar from "./CalendarToolbar";
import { RBCEventCustom } from "./RectBigCalendarCustomComponents/RBCEventCustom";
import { RBCEventWrapperCustom } from "./RectBigCalendarCustomComponents/RBCEventWrapperCustom";
import { RBCShowMoreButtonCustom } from "./RectBigCalendarCustomComponents/RBCShowMoreButtonCustom";
import RBCTimeIndicatorCustom from "./RectBigCalendarCustomComponents/RBCTimeIndicatorCustom";
import { YearCalendarViewRBC } from "./YearCalendarView";

// Set week start to Monday for all calendar views
moment.updateLocale("en", { week: { dow: 1 } });

// Docs: https://jquense.github.io/react-big-calendar/examples/index.html?path=/story/about-big-calendar--page

// Setup moment localizer for react-big-calendar
const calendarLocalizer = momentLocalizer(moment);

const minTimeOnDayAndWeekViews = new Date(1972, 0, 1, 0, 0, 0, 0);
const maxTimeOnDayAndWeekViews = new Date(1972, 0, 1, 23, 59, 59);
const agendaViewLengthDays = 30;
const selectableTimeIncrementsOnWeekAndDayViews = 30;
const isShowMoreEntriesInPopup = true;

export function rbcEventPropGetterFactory<TEntry extends GenericCalendarEntry>(params: {
  theme: Theme;
}): EventPropGetter<TEntry> {
  return function (entry: TEntry, start: Date, end: Date, isSelected: boolean) {
    return {
      className: undefined,
      style: {
        backgroundColor: entry.style?.backgroundColor || params.theme.palette.secondary.main,
        borderRadius: "10px",
        color: entry.style?.color || params.theme.palette.text.main,
        border: "none",
        padding: "2px 8px",
        fontWeight: 500,
        fontSize: 12, // reduced font size for event content
        boxShadow: "0 1px 4px rgba(0,0,0,0.20)",
      },
    } satisfies ReturnType<EventPropGetter<TEntry>>;
  };
}

export const CalendarView = {
  // RBCalendar views
  Month: Views.MONTH,
  Week: Views.WEEK,
  WorkWeek: Views.WORK_WEEK,
  Day: Views.DAY,
  Agenda: Views.AGENDA,

  // custom views
  Year: "year",
} as const;
export type CalendarView = (typeof CalendarView)[keyof typeof CalendarView];
export const allCalendarViews: CalendarView[] = Object.values(CalendarView);

type RBCalendarViewsProps = Record<CalendarView, boolean | (React.ComponentType<any> & ViewStatic)>;

const rbcFormats: Formats = {
  // Use 24h format for time gutter and event times
  timeGutterFormat: (date, culture, localizer) => localizer!.format(date, "HH:mm", culture),
  eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
    `${localizer!.format(start, "HH:mm", culture)} – ${localizer!.format(end, "HH:mm", culture)}`,
  agendaTimeFormat: (date, culture, localizer) => localizer!.format(date, "HH:mm", culture),
  // dateFormat: (date, culture, localizer) => localizer!.format(date, "dd", culture),
  // dayFormat: (date, culture, localizer) => localizer!.format(date, "DDD", culture),
  // dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
  //   localizer.format(start, { date: "short" }, culture) +
  //   " — " +
  //   localizer.format(end, { date: "short" }, culture),
};

export const rbcMessages = {
  date: "Date",
  time: "Time",
  event: "Event",
  allDay: "All day",
  week: "Week",
  work_week: "Work week",
  day: "Day",
  month: "Month",
  previous: "Back",
  next: "Next",
  yesterday: "Yesterday",
  tomorrow: "Tomorrow",
  today: "Today",
  agenda: "Agenda",
  year: "Year",
  noEventsInRange: "There are no events in this range.",
  /**
   * params {total} count of remaining events
   * params {remainingEvents} remaining events
   * params {events} all events in day
   */
  showMore: (total, remainingEvents, events) => `+${remainingEvents.length} more`,
} satisfies Messages<GenericCalendarEntry> & { year: string };

/** Generic calendar entry (aka. event). Agnostic of the calendar implementation. */
export interface GenericCalendarEntry<TData = any> extends RBCEvent {
  id: string;
  title: string | ReactNode;
  start: Date;
  end: Date;
  /** For HTML title or tooltip. */
  titleStr?: string;
  isAllDay?: boolean;
  style?: {
    backgroundColor?: string;
    color?: string;
  };
  resource?: any;
  data?: TData;
}

export interface CalendarProps<TEntry extends GenericCalendarEntry> {
  entries: TEntry[];
  /** Views to display. */
  views?: CalendarView[];
  /** View (month, week, day). */
  defaultView?: CalendarView;
  /** Date to display. */
  defaultDate?: Date;
  /** Whether the calendar is in read-only mode. */
  isReadOnly?: boolean;
  isLoading?: boolean;
  renderEntryTitle?: (entry: TEntry) => ReactNode;
  renderEntryPopoverContent?: (params: { entry: TEntry; close: () => void }) => ReactNode;
  /** Callback when view changes. */
  onViewChange?: (newView: CalendarView) => void;
  /** Callback when date navigation occurs (back, next, today, drill down to date). */
  onDateChange?: (newValue: { date: Date; view: CalendarView }) => void;
  /** Callback when visible date range changes (when view changes). */
  onViewDateRangeChange?: (newValue: {
    dateRangeInclusive: DateRange;
    dateRangeExclusive: DateRange;
    view: CalendarView;
  }) => void;
  /** Callback when visible date range changes by any means and entries should be fetched. */
  onDateRangeChange?: (newValue: {
    dateRangeInclusive: DateRange;
    dateRangeExclusive: DateRange;
    view: CalendarView;
  }) => void;
  /** Callback when an entry is selected (i.e.: an onClick handler). */
  onEntryClick?: (entry: TEntry) => void;
  /** Callback when a slot (date range) is selected. */
  onSlotClick?: (newValue: { range: DateRange; view: CalendarView }) => void;
}

/** NB: RBCalendar uses inclusive date ranges internally. */
export function Calendar<TEntry extends GenericCalendarEntry>(props: CalendarProps<TEntry>) {
  const {
    views = [CalendarView.Month, CalendarView.Week, CalendarView.Day, CalendarView.Year],
    isReadOnly = false,
    isLoading = false,
    renderEntryPopoverContent,
  } = props;

  const theme = useTheme();

  const calendar = useCalendar({ calendarProps: props });
  const calendarContext = useMemo(() => new CalendarContextValue({ calendar }), [calendar]);

  const eventPropGetter = useMemo<EventPropGetter<TEntry>>(
    () => rbcEventPropGetterFactory({ theme }),
    [theme],
  );

  // Custom wrapper to inject eventPropGetter into YearCalendarViewRBC and preserve static properties
  function YearCalendarViewWithEventProps(props2: any) {
    return (
      <YearCalendarViewRBC
        {...props2}
        eventPropGetter={eventPropGetter}
        onDrilldown={calendar.handleYearViewDrilldown}
      />
    );
  }
  // eslint-disable-next-line react-hooks/immutability
  YearCalendarViewWithEventProps.title = YearCalendarViewRBC.title;
  // eslint-disable-next-line react-hooks/immutability
  YearCalendarViewWithEventProps.navigate = YearCalendarViewRBC.navigate;
  // @ts-expect-error: react-big-calendar custom view static 'range' property is required at runtime
  // eslint-disable-next-line react-hooks/immutability
  YearCalendarViewWithEventProps.range = YearCalendarViewRBC.range;

  const viewsProps = useMemo<RBCalendarViewsProps>(
    () => ({
      [CalendarView.Year]: YearCalendarViewWithEventProps,
      [CalendarView.Month]: true,
      [CalendarView.Week]: true,
      [CalendarView.WorkWeek]: true,
      [CalendarView.Day]: true,
      [CalendarView.Agenda]: true,
    }),
    [views],
  );

  // const eventIdAccessor = useCallback((entry: TEntry) => entry.id, []);
  const startAccessor = useCallback((entry: TEntry) => entry.start, []);
  const endAccessor = useCallback((entry: TEntry) => entry.end, []);
  const allDayAccessor = useCallback(
    (entry: TEntry) => entry.isAllDay ?? entry.allDay ?? false,
    [],
  );
  const titleAccessor = useCallback((entry: TEntry) => entry.title as string, []);
  const tooltipAccessor = useCallback((entry: TEntry) => entry.titleStr as string, []);
  const resourceIdAccessor = undefined;
  const resourceTitleAccessor = undefined;

  return (
    <CalendarContext.Provider
      value={calendarContext as unknown as CalendarContextValue<GenericCalendarEntry>}
    >
      <Stack gap={1}>
        {isLoading && <LinearProgress />}

        <Box sx={{ height: "fit-content" }}>
          {/* NB: React Big Calendar requires all props to be memoized to avoid performance issues. */}
          {rbcGlobalStyles}
          <RBCalendar<TEntry, any>
            style={{ height: "100%" }}
            events={calendar.entries}
            localizer={calendarLocalizer}
            culture={undefined}
            formats={rbcFormats}
            messages={rbcMessages}
            defaultView={Views.MONTH}
            view={calendar.currentView as View}
            views={viewsProps}
            defaultDate={calendar.nowDate}
            date={calendar.currentDate}
            // getNow={() => new Date()}
            dayLayoutAlgorithm='overlap'
            min={minTimeOnDayAndWeekViews}
            max={maxTimeOnDayAndWeekViews}
            length={agendaViewLengthDays}
            step={selectableTimeIncrementsOnWeekAndDayViews}
            toolbar
            popup={isShowMoreEntriesInPopup}
            selectable={isReadOnly ? false : "ignoreEvents"}
            showAllEvents={false}
            showMultiDayTimes={false}
            rtl={false}
            drilldownView={CalendarView.Day}
            getDrilldownView={undefined}
            selected={undefined}
            longPressThreshold={250} // ms
            // eventIdAccessor={eventIdAccessor}
            startAccessor={startAccessor}
            endAccessor={endAccessor}
            allDayAccessor={allDayAccessor}
            titleAccessor={titleAccessor}
            tooltipAccessor={tooltipAccessor}
            resourceIdAccessor={resourceIdAccessor}
            resourceTitleAccessor={resourceTitleAccessor}
            eventPropGetter={eventPropGetter}
            // slotPropGetter={slotPropGetter}
            components={{
              toolbar: CalendarToolbar,
              event: RBCEventCustom, // renders inside of event: .rbc-event > .rbc-event-content > .event-custom
              eventWrapper: RBCEventWrapperCustom, // renders outside of event: .event-wrapper-custom > .rbc-event > .rbc-event-content
              showMore: RBCShowMoreButtonCustom,
              // @ts-expect-error: lib has trash types
              timeIndicatorWrapper: RBCTimeIndicatorCustom,
            }}
            onView={calendar.handleViewChange}
            onNavigate={calendar.handleDateChange}
            onRangeChange={calendar.handleViewDateRangeChange}
            onSelectEvent={calendar.handleSelectEvent}
            onSelectSlot={calendar.handleSelectSlot}
            // onShowMore={}
            // onSelecting={}
            // onDoubleClickEvent={}
          />
        </Box>

        {/* Entry details popover */}
        {calendar.entryToView && (
          <AppPopover
            open={Boolean(calendar.entryViewPopoverAnchorEl)}
            anchorEl={calendar.entryViewPopoverAnchorEl}
            onClose={() => {
              calendar.closeEntryViewPopover({ entry: calendar.entryToView });
            }}
          >
            {(params) =>
              renderEntryPopoverContent?.({
                entry: calendar.entryToView!,
                close: params.close,
              }) || (
                <CalendarEntryPopoverContent>
                  <CalendarEntryDetails entry={calendar.entryToView!} />
                </CalendarEntryPopoverContent>
              )
            }
          </AppPopover>
        )}
      </Stack>
    </CalendarContext.Provider>
  );
}
